import React, { useState, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import {
  reservationSlotTemplateRepository,
  reservationSlotSettingsRepository,
  reservationSlotRepository,
} from '../../../../infrastructure/repositories/reservationSlotRepositoryProvider';
import { monthlyShiftSlotSummaryRepository } from '../../../../infrastructure/repositories/MonthlyShiftSlotSummaryRepositoryImpl';

import { useTemplateManagement } from '../hooks/useTemplateManagement';
import { useReservationSlotSettings } from '../hooks/useReservationSlotSettings';
import { useReservationSlotGeneration } from '../hooks/useReservationSlotGeneration';
import { useReservationSlotSummary } from '../hooks/useReservationSlotSummary';
import { useConfirmedShiftMonths } from '../hooks/useConfirmedShiftMonths';

import ConfirmedShiftMonthTable from '../components/ConfirmedShiftMonthTable';
import TemplateListTable from '../components/TemplateListTable';
import TemplateEditDialog from '../components/TemplateEditDialog';
import SettingsPanel from '../components/SettingsPanel';
import SettingsDialog from '../components/SettingsDialog';
import SummaryPanel from '../components/SummaryPanel';
import SetupView from '../components/SetupView';

import {
  PageContainer,
  PageHeader,
  PillTabs,
  PillTab,
  SectionHeader,
  TabPanelContainer,
  LoadingOverlay,
} from '../ReservationSlotManagement.styles';

/**
 * タブの並び順
 *
 * @description
 * HTMLモックのサブナビと同じ並びにする。「解放枠生成」は独立したタブではなく、
 * 確定済みシフト・解放枠一覧から月を選んで開く設定ビュー（SetupView）の STEP 3 として提供する。
 */
const TAB_CONFIRMED_SHIFTS = 0;
const TAB_SLOT_SUMMARY = 1;
const TAB_TEMPLATES = 2;
const TAB_SETTINGS = 3;

/**
 * タブごとの表示名と補足説明
 */
const TAB_META = [
  {
    label: '確定済みシフト',
    description: 'ステータスが「確定」の月次シフトから解放枠を設定します',
  },
  {
    label: '解放枠一覧',
    description: '月ごとに生成済みの解放枠を確認・設定できます',
  },
  {
    label: '基本設定テンプレート',
    description: '解放枠の生成パターンを定義します',
  },
  {
    label: '基本設定',
    description: '医師ごとのテンプレート割り当てを管理します',
  },
] as const;

/**
 * 解放枠設定ビューの対象月と遷移元
 *
 * @property {number} year - 設定対象の年
 * @property {number} month - 設定対象の月（1-12）
 * @property {number} originTab - 遷移元のタブindex（戻る先の決定に使う）
 */
interface SetupTarget {
  year: number;
  month: number;
  originTab: number;
}

/**
 * 解放枠管理ページ
 *
 * @description
 * 解放枠（予約枠）管理機能のエントリポイント。
 * 確定済みシフト・解放枠一覧・基本設定テンプレート・基本設定の4タブを提供し、
 * 確定済みシフトまたは解放枠一覧から月を選ぶと、その月に閉じた解放枠設定ビューへ遷移する。
 * Infrastructure層のRepository実装を各フックへ注入する（依存性逆転）。
 */
export const ReservationSlotManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(TAB_CONFIRMED_SHIFTS);
  const [setupTarget, setSetupTarget] = useState<SetupTarget | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  const templateManagement = useTemplateManagement(reservationSlotTemplateRepository);
  const settings = useReservationSlotSettings(reservationSlotSettingsRepository);
  const generation = useReservationSlotGeneration(
    reservationSlotRepository,
    settings.defaultTemplateId,
    settings.overrides
  );
  const summary = useReservationSlotSummary(reservationSlotRepository);
  const confirmedShiftMonths = useConfirmedShiftMonths(monthlyShiftSlotSummaryRepository);

  const { setYearMonth } = generation;

  /**
   * 指定した月の解放枠設定ビューを開く
   *
   * @param {number} year - 設定対象の年
   * @param {number} month - 設定対象の月（1-12）
   */
  const handleOpenSetup = useCallback(
    (year: number, month: number) => {
      setYearMonth(year, month);
      setSetupTarget({ year, month, originTab: activeTab });
    },
    [setYearMonth, activeTab]
  );

  /**
   * 解放枠設定ビューを閉じて遷移元タブへ戻る
   */
  const handleBackFromSetup = useCallback(() => {
    if (setupTarget) {
      setActiveTab(setupTarget.originTab);
    }
    setSetupTarget(null);
  }, [setupTarget]);

  /**
   * 解放枠設定ビューから「基本設定テンプレート」タブへ移動する
   */
  const handleOpenTemplatesTab = useCallback(() => {
    setSetupTarget(null);
    setActiveTab(TAB_TEMPLATES);
  }, []);

  const isSetupOpen = setupTarget !== null;

  return (
    <PageContainer>
      <PageHeader>
        <Typography variant="h5" component="h1" fontWeight={600}>
          解放枠管理
        </Typography>
      </PageHeader>

      {isSetupOpen && setupTarget ? (
        <SetupView
          year={setupTarget.year}
          month={setupTarget.month}
          originLabel={TAB_META[setupTarget.originTab].label}
          templates={templateManagement.templates}
          settings={settings}
          generation={generation}
          onBack={handleBackFromSetup}
          onOpenTemplates={handleOpenTemplatesTab}
          onEditTemplate={templateManagement.openEditDialog}
          onOpenSettings={() => setSettingsDialogOpen(true)}
        />
      ) : (
        <>
          <PillTabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
            {TAB_META.map((tab) => (
              <PillTab key={tab.label} label={tab.label} />
            ))}
          </PillTabs>

          {/* タブ見出し（右端にタブ固有のアクションを置く） */}
          <SectionHeader>
            <Box>
              <Typography variant="h6" component="h2" fontWeight={700}>
                {TAB_META[activeTab].label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {TAB_META[activeTab].description}
              </Typography>
            </Box>
            {activeTab === TAB_TEMPLATES && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={templateManagement.openCreateDialog}
              >
                新規作成
              </Button>
            )}
            {activeTab === TAB_SETTINGS && (
              <Button
                variant="contained"
                onClick={settings.save}
                disabled={settings.saving || settings.isLoading}
              >
                保存する
              </Button>
            )}
          </SectionHeader>

          <TabPanelContainer>
            {/* 確定済みシフトタブ */}
            {activeTab === TAB_CONFIRMED_SHIFTS && (
              <Box sx={{ position: 'relative' }}>
                {confirmedShiftMonths.isLoading && (
                  <LoadingOverlay>
                    <CircularProgress />
                  </LoadingOverlay>
                )}
                {confirmedShiftMonths.error && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    onClose={confirmedShiftMonths.clearError}
                  >
                    {confirmedShiftMonths.error}
                  </Alert>
                )}
                <ConfirmedShiftMonthTable
                  months={confirmedShiftMonths.months}
                  page={confirmedShiftMonths.page}
                  rowsPerPage={confirmedShiftMonths.rowsPerPage}
                  totalCount={confirmedShiftMonths.totalCount}
                  onPageChange={confirmedShiftMonths.setPage}
                  onOpenSetup={handleOpenSetup}
                />
              </Box>
            )}

            {/* 解放枠一覧タブ */}
            {activeTab === TAB_SLOT_SUMMARY && (
              <SummaryPanel summary={summary} onOpenSetup={handleOpenSetup} />
            )}

            {/* 基本設定テンプレートタブ */}
            {activeTab === TAB_TEMPLATES && (
              <Box sx={{ position: 'relative' }}>
                {templateManagement.isLoading && (
                  <LoadingOverlay>
                    <CircularProgress />
                  </LoadingOverlay>
                )}
                {templateManagement.error && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={templateManagement.clearError}>
                    {templateManagement.error}
                  </Alert>
                )}
                <TemplateListTable
                  templates={templateManagement.templates}
                  onEdit={templateManagement.openEditDialog}
                  onDelete={templateManagement.openDeleteDialog}
                />
              </Box>
            )}

            {/* 基本設定タブ */}
            {activeTab === TAB_SETTINGS && (
              <SettingsPanel templates={templateManagement.templates} settings={settings} />
            )}
          </TabPanelContainer>
        </>
      )}

      {/* 基本設定ダイアログ（解放枠設定ビューのSTEP 2から開く） */}
      <SettingsDialog
        open={settingsDialogOpen}
        templates={templateManagement.templates}
        settings={settings}
        onClose={() => setSettingsDialogOpen(false)}
      />

      {/* テンプレート編集ダイアログ */}
      <TemplateEditDialog
        open={templateManagement.dialogOpen}
        initial={templateManagement.editingTemplate}
        saving={templateManagement.saving}
        onClose={templateManagement.closeDialog}
        onSave={templateManagement.save}
      />

      {/* テンプレート削除確認ダイアログ */}
      <Dialog open={templateManagement.deleteDialogOpen} onClose={templateManagement.closeDeleteDialog}>
        <DialogTitle>テンプレートの削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{templateManagement.deletingTemplate?.name}」を削除します。よろしいですか？
            <br />
            ※基本設定・個別上書きから参照中の場合は削除できません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={templateManagement.closeDeleteDialog} disabled={templateManagement.saving}>
            キャンセル
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={templateManagement.confirmDelete}
            disabled={templateManagement.saving}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

ReservationSlotManagementPage.displayName = 'ReservationSlotManagementPage';

export default ReservationSlotManagementPage;
